import { useState } from 'react';
import { useNavigate } from 'react-router';
import { getSupabaseClient } from '@kordbooks/supabase-client';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { UntitledLogo } from '@/components/foundations/logo/untitledui-logo';
import { validateEmail } from '@kordbooks/shared-auth';
import { ArrowLeft } from '@untitledui/icons';



type Step = 'request' | 'code';

export default function ForgotPasswordRequest() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError('Email is required');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      const supabase = getSupabaseClient();

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        trimmedEmail,
        {
          // Optional: keep a magic-link as a fallback
          redirectTo: `${window.location.origin}/forgot-password-reset`,
        }
      );

      if (resetError) {
        throw resetError;
      }

      // Move to code-entry step
      setStep('code');
    } catch (err: any) {
      setError(
        err?.message || 'Failed to send reset email. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError('Verification code is required');
      return;
    }

    if (!/^\d{6}$/.test(trimmedCode)) {
      setError('Code must be a 6-digit number');
      return;
    }

    try {
      setIsLoading(true);
      const supabase = getSupabaseClient();

      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: trimmedCode,
        type: 'recovery',
      });

      if (verifyError) {
        throw verifyError;
      }

      // Successful verification creates a recovery session; go to reset page
      navigate('/forgot-password-reset');
    } catch (err: any) {
      setError(
        err?.message || 'Invalid or expired code. Please request a new one.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    if (step === 'request') {
      await handleRequestSubmit(e);
    } else {
      await handleCodeSubmit(e);
    }
  };

  return (
    <div className="flex min-h-dvh items-center justify-center bg-primary px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex justify-center">
          <UntitledLogo className="h-8" />
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-display-sm font-semibold text-primary md:text-display-md">
            {step === 'request' ? 'Reset password' : 'Enter recovery code'}
          </h1>
          <p className="mt-2 text-sm text-tertiary">
            {step === 'request'
              ? 'Enter your email and we’ll send you a recovery code.'
              : 'Enter the 6-digit code that was sent to your email address.'}
          </p>
        </div>

        {error && (
          <div className="mb-6 rounded-lg bg-error-primary/10 p-4 text-sm text-error-primary">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-sm font-medium text-primary"
            >
              Email address
            </label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(value: string) => {
                setEmail(value);
                setError(null);
              }}
              isInvalid={!!error && step === 'request'}
              isDisabled={isLoading || step === 'code'}
            />
          </div>

          {step === 'code' && (
            <div className="space-y-2">
              <label
                htmlFor="recovery-code"
                className="block text-sm font-medium text-primary"
              >
                Recovery code
              </label>
              <Input
                id="recovery-code"
                type="text"
                inputMode="numeric"
                pattern="\d*"
                placeholder="000000"
                value={code}
                onChange={(value: string) => {
                  setCode(value);
                  setError(null);
                }}
                maxLength={6}
                isInvalid={!!error}
                isDisabled={isLoading}
                className="font-mono tracking-widest text-center text-lg"
              />
              <p className="text-xs text-tertiary">
                The code expires after a short time; request a new one if it no
                longer works.
              </p>
            </div>
          )}

          <Button type="submit" className="w-full" isDisabled={isLoading}>
            {isLoading
              ? step === 'request'
                ? 'Sending...'
                : 'Verifying...'
              : step === 'request'
              ? 'Send recovery code'
              : 'Verify code'}
          </Button>
        </form>

        <Button
          className="mt-4 w-full"
          onClick={() => {
            window.location.href = '/sign-in';
          }}
        >
          Back to log in
        </Button>

        <div className="mt-6 flex justify-center">
          <a
            href="/sign-in"
            className="flex items-center gap-2 text-sm font-medium text-tertiary hover:text-primary"
          >
            <ArrowLeft className="size-4" />
            Back to log in
          </a>
        </div>
      </div>
    </div>
  );
}
